import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import bankService from "../../../services/bank.service";
import "../../../styles/auth.css";
import "../../../styles/users.css";

const initial = {
  bankName: "",
  email: "",
  phone: "",
  address: "",
  city: "",
  state: "",
  adminName: "",
  adminPassword: "",
};

export default function BankRegister() {
  const [form, setForm] = useState(initial);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [loading, setLoading] = useState(false);
  const [registrationId, setRegistrationId] = useState("");
  const [otpSent, setOtpSent] = useState(false);
  const [otp, setOtp] = useState("");
  const [resendCooldown, setResendCooldown] = useState(0);

  useEffect(() => {
    if (resendCooldown <= 0) return undefined;
    const timer = window.setInterval(() => {
      setResendCooldown((value) => Math.max(0, value - 1));
    }, 1000);
    return () => window.clearInterval(timer);
  }, [resendCooldown]);

  const handleChange = (e) => setForm((p) => ({ ...p, [e.target.name]: e.target.value }));

  const resetOtpFlow = () => {
    setOtpSent(false);
    setRegistrationId("");
    setOtp("");
    setResendCooldown(0);
    setError("");
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setSuccess("");
    if (form.adminPassword.length < 8) {
      setError("Password must be at least 8 characters");
      return;
    }
    if (form.phone.replace(/\D/g, "").length < 10) {
      setError("Phone must be a valid 10-digit mobile number.");
      return;
    }
    setLoading(true);
    try {
      const response = await bankService.register(form);
      setRegistrationId(response?.data?.registrationId || "");
      setOtpSent(Boolean(response?.requiresOtp));
      setResendCooldown(Number(response?.data?.resendAvailableInSeconds || 60));
    } catch (err) {
      setError(err?.response?.data?.message || "Registration failed");
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyOtp = async (e) => {
    e.preventDefault();
    if (!/^\d{6}$/.test(otp)) {
      setError("Enter the 6-digit OTP received by SMS.");
      return;
    }
    setLoading(true);
    setError("");
    try {
      const response = await bankService.verifyRegistrationOtp(registrationId, otp);
      setOtpSent(false);
      setOtp("");
      setSuccess(response?.data?.message || response?.message || "Bank registration submitted successfully.");
      setForm(initial);
    } catch (err) {
      setError(err?.response?.data?.message || "OTP verification failed.");
    } finally {
      setLoading(false);
    }
  };

  const handleResendOtp = async () => {
    if (loading || resendCooldown > 0) return;
    setLoading(true);
    setError("");
    try {
      const response = await bankService.resendRegistrationOtp(registrationId);
      setResendCooldown(Number(response?.data?.resendAvailableInSeconds || 60));
    } catch (err) {
      setError(err?.response?.data?.message || "Unable to resend OTP.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="page">
      <div className="create-company-card" style={{ maxWidth: 600, margin: "40px auto", padding: 32 }}>
        <h2 style={{ marginBottom: 24 }}>Register Your Bank</h2>

        {success ? (
          <div>
            <p style={{ color: "green", marginBottom: 16 }}>{success}</p>
            <Link to="/bank/login" className="primary-page-btn">
              Go to Login
            </Link>
          </div>
        ) : (
          otpSent ? (
            <form onSubmit={handleVerifyOtp}>
              <h3>Verify Mobile Number</h3>
              <p className="muted">
                We sent an OTP to {form.phone.replace(/\d(?=\d{4})/g, "*")}.
              </p>
              <div className="form-group">
                <label htmlFor="bank-registration-otp">Enter OTP</label>
                <input
                  id="bank-registration-otp"
                  type="text"
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  maxLength={6}
                  autoFocus
                  value={otp}
                  onChange={(e) => setOtp(e.target.value.replace(/\D/g, "").slice(0, 6))}
                  placeholder="6-digit OTP"
                />
              </div>
              {resendCooldown > 0 ? <p className="muted">Resend available in {resendCooldown}s</p> : null}
              {error && <p className="error-text">{error}</p>}
              <div style={{ display: "flex", gap: 12, marginTop: 24 }}>
                <button type="button" className="secondary-page-btn" onClick={resetOtpFlow} disabled={loading}>Back</button>
                <button type="button" className="secondary-page-btn" onClick={handleResendOtp} disabled={loading || resendCooldown > 0}>Resend OTP</button>
                <button type="submit" className="primary-page-btn" disabled={loading || otp.length !== 6}>{loading ? "Verifying..." : "Verify OTP"}</button>
              </div>
            </form>
          ) : (
          <form onSubmit={handleSubmit}>
            <div className="form-grid two-column">
              <div className="form-group">
                <label>Bank Name *</label>
                <input name="bankName" value={form.bankName} onChange={handleChange} required placeholder="Enter bank name" />
              </div>
              <div className="form-group">
                <label>Email *</label>
                <input type="email" name="email" value={form.email} onChange={handleChange} required placeholder="Bank email" />
              </div>
              <div className="form-group">
                <label>Phone *</label>
                <input type="tel" name="phone" value={form.phone} onChange={handleChange} required placeholder="10-digit number" />
              </div>
              <div className="form-group">
                <label>City</label>
                <input name="city" value={form.city} onChange={handleChange} placeholder="City" />
              </div>
              <div className="form-group">
                <label>State</label>
                <input name="state" value={form.state} onChange={handleChange} placeholder="State" />
              </div>
              <div className="form-group full-width">
                <label>Address</label>
                <input name="address" value={form.address} onChange={handleChange} placeholder="Bank address" />
              </div>
              <div className="form-group">
                <label>Admin Name *</label>
                <input name="adminName" value={form.adminName} onChange={handleChange} required placeholder="Your full name" />
              </div>
              <div className="form-group">
                <label>Password * (min 8 chars)</label>
                <input
                  type="password"
                  name="adminPassword"
                  value={form.adminPassword}
                  onChange={handleChange}
                  required
                  minLength={8}
                  placeholder="Create a password"
                  autoComplete="new-password"
                />
              </div>
            </div>

            {error && <p className="error-text" style={{ marginTop: 12 }}>{error}</p>}

            <div style={{ display: "flex", gap: 12, marginTop: 24 }}>
              <button type="submit" className="primary-page-btn" disabled={loading}>
                {loading ? "Submitting..." : "Submit Registration"}
              </button>
              <Link to="/bank/login" className="secondary-page-btn">
                Back to Login
              </Link>
            </div>
          </form>
          )
        )}
      </div>
    </div>
  );
}
